import { useEffect, useState } from 'react'
import { useValorComPausa } from '../../componentes/pausa'
import { useSearchParams } from 'react-router-dom'
import {
  ROTULO_STATUS_USUARIO,
  type MembroDaEquipeDto,
  type UsuarioSessao,
} from '@crm/shared'
import { ErroRequisicao } from '../../api/cliente'
import { useEquipe, useEu, useSetores } from '../../api/hooks'
import { useFunis, useTags } from '../../api/hooks-leads'
import {
  useAdicionarApoio,
  useAgendarRetorno,
  useChamado,
  useConexoes,
  useConversaAvulsa,
  useContagens,
  useConversas,
  useEncerrarChamado,
  useMarcarLeitura,
  useTransferirChamado,
} from '../../api/hooks-chat'
import { Aviso, Botao, Campo, Carregando, Modal, juntar } from '../../componentes/ui'
import { CabecalhoDaPagina } from '../../componentes/pagina'
import { useTempoReal } from '../../tempo-real/socket'
import { marcarConversaAberta } from '../../tempo-real/som'
import { Conversa } from './Conversa'
import { NovaConversa } from './NovaConversa'
import { pode } from '../../permissao'
import { useEstadoLembrado } from '../../componentes/memoria-de-tela'
import { BarraDaLista } from './BarraDaLista'
import { ListaConversas } from './ListaConversas'
import { FILTRO_VAZIO, FiltrosDaLista, type ValoresDoFiltro } from './FiltrosDaLista'
import { AvisoForaDoHorario } from '../../componentes/FimDoHorario'
import { paraCampoDataHora } from '../../formato'

export function BatePapo({ usuario }: { usuario: UsuarioSessao }) {
  const { data: conexoes, isLoading: carregandoConexoes } = useConexoes()
  /**
   * O que precisa sobreviver a sair da tela e voltar: o número, o recorte, a
   * conversa aberta e os filtros. Voltar com a lista sem filtro mas com a
   * conversa antiga selecionada seria pior que não lembrar nada — o cartão
   * some da lista e o painel mostra alguém que não está ali.
   */
  const [conexaoId, setConexaoId] = useEstadoLembrado<string | null>('chat.conexao', null)
  const [escopoLembrado, setEscopo] = useEstadoLembrado<'todas' | 'minhas' | 'fila'>(
    'chat.escopo',
    'todas',
  )
  /*
    Fila e Todas têm permissão própria (ADR 0038). A aba lembrada que a pessoa
    não pode mais abrir cai em Minhas, que é sempre dela.
  */
  const escoposPermitidos: ('todas' | 'minhas' | 'fila')[] = [
    'minhas',
    ...(pode(usuario, 'bate_papo.fila') ? (['fila'] as const) : []),
    ...(pode(usuario, 'bate_papo.todas') ? (['todas'] as const) : []),
  ]
  const escopo = escoposPermitidos.includes(escopoLembrado) ? escopoLembrado : 'minhas'
  const [status, setStatus] = useEstadoLembrado<'aberto' | 'fechado' | 'todas'>(
    'chat.status',
    'aberto',
  )
  const [busca, setBusca] = useState('')
  const [filtros, setFiltros] = useEstadoLembrado<ValoresDoFiltro>('chat.filtros', FILTRO_VAZIO)
  const [filtrosAbertos, setFiltrosAbertos] = useState(false)
  const [iniciandoConversa, setIniciandoConversa] = useState(false)

  // Listas que alimentam os filtros. São pequenas e ficam em cache.
  const { data: usuarios } = useEquipe()
  const { data: tags } = useTags()
  const { data: funis } = useFunis()
  /**
   * Guarda só o id, não a conversa inteira.
   *
   * Guardando o objeto, a tela ficava com uma cópia congelada do momento do
   * clique: marcar como não lida gravava no banco, a lista se atualizava, e o
   * painel da direita continuava mostrando o estado antigo — a caixinha não
   * mudava e parecia que o clique não tinha funcionado.
   */
  const [chamadoSelecionado, setChamadoSelecionado] = useEstadoLembrado<string | null>(
    'chat.conversa',
    null,
  )

  /*
    `?chamado=<id>` na URL abre aquela conversa.

    É por aqui que chega quem clica em "abrir a conversa" num aviso de
    cronômetro ou numa notificação. Antes o link levava só a `/bate-papo`, sem
    dizer qual — e estando já no bate-papo, o clique não fazia nada.

    O parâmetro é consumido: depois de selecionar, ele sai da URL, senão um F5
    ou o botão de voltar reabririam a conversa antiga por cima da escolhida.
  */
  const [parametros, setParametros] = useSearchParams()
  const chamadoNaUrl = parametros.get('chamado')
  /*
    A mensagem que a outra tela quer mostrar.

    Quem vem de outra tela clicou numa mensagem específica; abrir a
    conversa no fim do histórico deixaria essa pessoa procurando qual das
    mensagens era a dela, que num cliente antigo é rolar meses para trás.
  */
  const mensagemNaUrl = parametros.get('mensagem')

  useEffect(() => {
    if (!chamadoNaUrl) return
    setChamadoSelecionado(chamadoNaUrl)
    if (mensagemNaUrl) setMensagemAlvo(mensagemNaUrl)
    parametros.delete('chamado')
    parametros.delete('mensagem')
    setParametros(parametros, { replace: true })
  }, [chamadoNaUrl, mensagemNaUrl])

  /*
    A mensagem que a busca achou, para a conversa abrir nela.

    Fica aqui, e não dentro da conversa, porque quem sabe qual é é o cartão
    clicado na lista. Trocar de conversa limpa o alvo: abrir outra pessoa não
    tem por que cair no meio do histórico dela.
  */
  const [mensagemAlvo, setMensagemAlvo] = useState<string | null>(null)

  const [encerrando, setEncerrando] = useState(false)
  const [transferindo, setTransferindo] = useState(false)
  const [apoiando, setApoiando] = useState(false)
  const [agendando, setAgendando] = useState(false)

  // A consulta espera a pausa na digitação; a caixa responde a cada tecla.
  const buscaComPausa = useValorComPausa(busca)

  const conexaoAtual =
    (conexoes ?? []).find((c) => c.id === conexaoId) ?? (conexoes ?? [])[0] ?? null

  useTempoReal(conexaoAtual?.id ?? null)

  const filtro = {
    conexaoId: conexaoAtual?.id ?? '',
    status,
    escopo,
    busca: buscaComPausa || undefined,
    protocolo: filtros.protocolo || undefined,
    mensagem: filtros.mensagem || undefined,
    responsavelId: filtros.responsavelId || undefined,
    tagId: filtros.tagId || undefined,
    funilId: filtros.funilId || undefined,
    etapaId: filtros.etapaId || undefined,
    // Fim do dia no "até", senão o próprio dia escolhido fica de fora.
    de: filtros.de ? new Date(`${filtros.de}T00:00:00`).toISOString() : undefined,
    ate: filtros.ate ? new Date(`${filtros.ate}T23:59:59`).toISOString() : undefined,
  }
  const { data: conversas, isLoading, error } = useConversas(filtro, Boolean(conexaoAtual))

  // Os três números das abas. Recarregam junto com a lista, então a fila
  // avisa sozinha quando entra conversa nova.
  const { data: contagens } = useContagens(filtro, Boolean(conexaoAtual))

  // Sempre a versão fresca da lista, nunca uma cópia guardada no clique.
  const naLista = (conversas ?? []).find((c) => c.chamadoId === chamadoSelecionado) ?? null

  /*
    A conversa procurada pode não estar na lista carregada: o filtro está em
    "abertos" e ela foi encerrada, ou em "minhas" e ela é da fila. Quem chega
    por link traz o chamado, não o filtro. Buscada à parte, ela abre do mesmo
    jeito — e a lista à esquerda continua mostrando o recorte escolhido.
  */
  const { data: avulsa } = useConversaAvulsa(
    chamadoSelecionado,
    Boolean(chamadoSelecionado) && !naLista && !isLoading,
  )
  const selecionada = naLista ?? avulsa ?? null

  // Mensagem na conversa que está na tela não toca som (ADR 0040).
  const abertaAgora = selecionada?.chamadoId ?? null
  useEffect(() => {
    marcarConversaAberta(abertaAgora)
    return () => marcarConversaAberta(null)
  }, [abertaAgora])

  const { data: chamado } = useChamado(chamadoSelecionado)
  const marcarLeitura = useMarcarLeitura()

  const erro = error instanceof ErroRequisicao ? error.corpo.mensagem : null

  if (carregandoConexoes) return <Carregando />

  if ((conexoes ?? []).length === 0) {
    return (
      // O bate-papo vai de borda a borda (sem o `p-6` das outras telas); sem conversa para mostrar, a folga volta.
      <div className="space-y-4 p-6">
        <CabecalhoDaPagina titulo="Bate-papo" />
        {/*
          Quem lê isto quase sempre é alguém da equipe sem número liberado, e
          não quem cadastra número (mapa de uso, 08/10/2026): para o vendedor,
          mandar à Configuração era mandar a uma tela que ele não abre.
        */}
        <Aviso tipo="atencao">
          {usuario.proprietario
            ? 'Nenhum número de WhatsApp cadastrado. Cadastre um em Configuração → Números e leia o QR code com o celular.'
            : 'Nenhum número de WhatsApp liberado para você. Peça a quem administra o CRM para liberar o número em que você vai trabalhar.'}
        </Aviso>
      </div>
    )
  }

  return (
    // `h-full` porque quem limita a altura agora é o Layout, que fixou o
    // menu e passou a rolar por coluna. Descontar 3rem da viewport aqui
    // sobraria uma faixa vazia embaixo.
    <div className="flex h-full min-h-0 flex-col">
      {erro ? (
        <div className="p-2">
          <Aviso tipo="erro">{erro}</Aviso>
        </div>
      ) : null}

      {/*
        As duas colunas encostadas, e agora encostadas também nas bordas da
        janela.

        Antes eram dois cartões com `gap-3` entre eles; depois uma moldura
        arredondada em volta das duas. A moldura era a última coisa fazendo o
        chat parecer um cartão flutuando no meio da tela em vez da tela
        inteira — some ela, some o canto arredondado, some o oito de padding.
      */}
      {/*
        No celular, uma coluna por vez, como no WhatsApp: a lista, ou a conversa com a seta de
        voltar. As duas lado a lado deixavam 55px para a conversa num telefone de 375px, e a caixa de
        escrever quebrava letra por letra.
      */}
      <div className="flex min-h-0 flex-1 overflow-hidden border-t border-tinta-200 bg-superficie">
        <aside
          className={juntar(
            'w-full shrink-0 flex-col overflow-hidden border-tinta-200 md:flex md:w-80 md:border-r',
            selecionada ? 'hidden' : 'flex',
          )}
        >
          {/*
            Número, recorte e situação vivem aqui, e não numa faixa acima da
            tela inteira: todos respondem à mesma pergunta, quais conversas
            estou olhando, e a faixa custava uma linha inteira de altura em
            cima do chat.
          */}
          <BarraDaLista
            escopo={escopo}
            aoTrocarEscopo={setEscopo}
            contagens={contagens}
            status={status}
            aoTrocarStatus={(s) => {
              setStatus(s)
              setChamadoSelecionado(null)
            }}
            busca={busca}
            aoBuscar={setBusca}
            conexoes={conexoes ?? []}
            conexaoAtual={conexaoAtual}
            aoTrocarConexao={(id) => {
              setConexaoId(id)
              setChamadoSelecionado(null)
            }}
            filtros={filtros}
            filtrosAbertos={filtrosAbertos}
            aoAlternarFiltros={() => setFiltrosAbertos((a) => !a)}
            aoLimparFiltros={() => setFiltros(FILTRO_VAZIO)}
            aoIniciarConversa={
              pode(usuario, 'bate_papo.iniciar') ? () => setIniciandoConversa(true) : undefined
            }
            escopos={escoposPermitidos}
          />

          <FiltrosDaLista
            valores={filtros}
            aoMudar={(novos) => {
              /*
                Procurar no que foi dito abre o recorte para "todas".

                O que se procura quase sempre foi dito numa conversa que já
                acabou, e a lista abre em "abertos": a conversa certa ficava de
                fora e a busca parecia não funcionar. O seletor muda à vista,
                e quem quiser só as abertas volta nele.
              */
              if (novos.mensagem && !filtros.mensagem) setStatus('todas')
              setFiltros(novos)
            }}
            aoLimpar={() => setFiltros(FILTRO_VAZIO)}
            usuarios={usuarios ?? []}
            tags={tags ?? []}
            funis={funis ?? []}
            status={status}
            aberto={filtrosAbertos}
          />

          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <Carregando />
            ) : (
              <ListaConversas
                conversas={conversas ?? []}
                escopo={escopo}
                usuarioId={usuario.id}
                selecionada={selecionada?.chamadoId ?? null}
                /*
                  Só seleciona. Marcar como lida ao abrir é feito pela própria
                  lista, junto com a pausa que segura o cartão no lugar — aqui
                  a mutação disparava direto e o cartão pulava de bloco sem o
                  esvaimento, só no caminho de abrir a conversa.
                */
                aoSelecionar={(conversa) => {
                  setChamadoSelecionado(conversa.chamadoId)
                  setMensagemAlvo(conversa.encontrada?.mensagemId ?? null)
                }}
                aoAlternarLeitura={(conversa, naoLida) =>
                  marcarLeitura.mutate({ chamadoId: conversa.chamadoId, naoLida })
                }
                listaMisturada={status === 'todas'}
              />
            )}
          </div>
        </aside>

        <main
          className={juntar('min-w-0 flex-1 overflow-hidden md:block', selecionada ? '' : 'hidden')}
        >
          {selecionada ? (
            <Conversa
              conversa={selecionada}
              chamado={chamado}
              usuario={usuario}
              aoVoltar={() => setChamadoSelecionado(null)}
              mensagemAlvo={mensagemAlvo}
              aoSoltarAlvo={() => setMensagemAlvo(null)}
              palavraBuscada={filtros.mensagem || null}
              aoEncerrar={() => setEncerrando(true)}
              aoTransferir={() => setTransferindo(true)}
              aoApoiar={() => setApoiando(true)}
              aoAgendarRetorno={() => setAgendando(true)}
            />
          ) : (
            <p className="p-10 text-center text-sm text-tinta-400">
              Escolha uma conversa à esquerda.
            </p>
          )}
        </main>
      </div>

      {selecionada ? (
        <>
          <ModalEncerrar
            chamadoId={selecionada.chamadoId}
            aberto={encerrando}
            aoFechar={() => setEncerrando(false)}
          />
          <ModalTransferir
            chamadoId={selecionada.chamadoId}
            aberto={transferindo}
            aoFechar={() => setTransferindo(false)}
          />
          <ModalApoio
            chamadoId={selecionada.chamadoId}
            aberto={apoiando}
            aoFechar={() => setApoiando(false)}
          />
          <ModalRetorno
            chamadoId={selecionada.chamadoId}
            atual={selecionada.retornoAgendadoPara}
            aberto={agendando}
            aoFechar={() => setAgendando(false)}
          />
        </>
      ) : null}

      <NovaConversa
        aberto={iniciandoConversa}
        conexoes={conexoes ?? []}
        conexaoAtual={conexaoAtual?.id ?? null}
        aoFechar={() => setIniciandoConversa(false)}
        aoAbrir={(chamadoId) => setChamadoSelecionado(chamadoId)}
      />
    </div>
  )
}

/** Pop-up de encerramento com as duas opções (4.2). */
function ModalEncerrar({
  chamadoId,
  aberto,
  aoFechar,
}: {
  chamadoId: string
  aberto: boolean
  aoFechar: () => void
}) {
  const encerrar = useEncerrarChamado()
  const erro = encerrar.error instanceof ErroRequisicao ? encerrar.error : null

  return (
    <Modal aberto={aberto} titulo="Encerrar atendimento" aoFechar={aoFechar}>
      <div className="space-y-3">
        {erro ? <Aviso tipo="erro">{erro.corpo.mensagem}</Aviso> : null}

        <button
          type="button"
          disabled={encerrar.isPending}
          onClick={() =>
            encerrar.mutate({ chamadoId, manterNaCarteira: true }, { onSuccess: aoFechar })
          }
          className="w-full rounded-md ring-1 ring-tinta-200 p-3 text-left hover:ring-marca hover:bg-marca/5"
        >
          <span className="block font-medium">Manter na carteira</span>
          <span className="block text-sm text-tinta-400">
            A próxima mensagem deste cliente volta direto para você, sem passar pela fila.
          </span>
        </button>

        <button
          type="button"
          disabled={encerrar.isPending}
          onClick={() =>
            encerrar.mutate({ chamadoId, manterNaCarteira: false }, { onSuccess: aoFechar })
          }
          className="w-full rounded-md ring-1 ring-tinta-200 p-3 text-left hover:ring-marca hover:bg-marca/5"
        >
          <span className="block font-medium">Apenas encerrar</span>
          <span className="block text-sm text-tinta-400">
            A próxima mensagem deste cliente cai na fila e vai para quem estiver na vez de atender.
          </span>
        </button>
      </div>
    </Modal>
  )
}

/**
 * O nome na lista de escolha, com o status: quem pede apoio ou transfere quer
 * saber se a pessoa está ali (mapa de uso, 08/10/2026). Quem está escolhendo
 * aparece como "(eu)", para se transferir a conversa.
 */
function nomeComStatus(u: MembroDaEquipeDto, euId?: string): string {
  const nome = u.id === euId ? `${u.nome} (eu)` : u.nome
  return `${nome} · ${ROTULO_STATUS_USUARIO[u.status]}`
}

/** Transferência (4.6); o comentário é opcional desde 08/10/2026. */
function ModalTransferir({
  chamadoId,
  aberto,
  aoFechar,
}: {
  chamadoId: string
  aberto: boolean
  aoFechar: () => void
}) {
  const transferir = useTransferirChamado()
  const { data: setores } = useSetores()
  const { data: usuarios } = useEquipe()
  const { data: eu } = useEu()
  const [setorId, setSetorId] = useState('')
  const [responsavelId, setResponsavelId] = useState('')
  const [comentario, setComentario] = useState('')

  const erro = transferir.error instanceof ErroRequisicao ? transferir.error : null

  return (
    <Modal
      aberto={aberto}
      titulo="Transferir atendimento"
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="primario"
            carregando={transferir.isPending}
            onClick={() =>
              transferir.mutate(
                {
                  chamadoId,
                  dados: {
                    // Vazio é "manter o setor atual" e "deixar na fila": o servidor entende assim.
                    setorId: setorId || null,
                    responsavelId: responsavelId || null,
                    comentario: comentario.trim() || null,
                  },
                },
                {
                  onSuccess: () => {
                    setComentario('')
                    aoFechar()
                  },
                },
              )
            }
          >
            Transferir
          </Botao>
        </>
      }
    >
      <div className="space-y-3">
        {erro ? <Aviso tipo="erro">{erro.corpo.mensagem}</Aviso> : null}

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-tinta-800">Setor</span>
          <select
            value={setorId}
            onChange={(e) => setSetorId(e.target.value)}
            className="w-full rounded-md ring-1 ring-tinta-200 bg-superficie px-3 py-2 text-sm"
          >
            <option value="">Manter o setor atual</option>
            {(setores ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.nome}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-tinta-800">Responsável</span>
          <select
            value={responsavelId}
            onChange={(e) => setResponsavelId(e.target.value)}
            className="w-full rounded-md ring-1 ring-tinta-200 bg-superficie px-3 py-2 text-sm"
          >
            <option value="">Deixar na fila do setor</option>
            {(usuarios ?? [])
              .filter((u) => u.ativo)
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {nomeComStatus(u, eu?.id)}
                </option>
              ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-tinta-800">
            Comentário (opcional)
          </span>
          <textarea
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            rows={3}
            placeholder="Cliente quer nota fiscal, passei para o financeiro."
            className="w-full rounded-md ring-1 ring-tinta-200 px-3 py-2 text-sm"
          />
          <span className="mt-1 block text-xs text-tinta-400">
            Vai para a linha do tempo da conversa, junto com quem transferiu e para quem.
          </span>
        </label>
      </div>
    </Modal>
  )
}

function ModalApoio({
  chamadoId,
  aberto,
  aoFechar,
}: {
  chamadoId: string
  aberto: boolean
  aoFechar: () => void
}) {
  const adicionar = useAdicionarApoio()
  const { data: usuarios } = useEquipe()
  const { data: eu } = useEu()
  // Quem já está de apoio aparece na janela (pedido de 05/10/2026), e sai da lista de escolha.
  const { data: chamado } = useChamado(aberto ? chamadoId : null)
  const apoiosAtuais = chamado?.apoios ?? []
  const pedidosAtuais = chamado?.pedidosDeApoio ?? []
  const [usuarioId, setUsuarioId] = useState('')
  const [comentario, setComentario] = useState('')

  const erro = adicionar.error instanceof ErroRequisicao ? adicionar.error : null

  return (
    <Modal
      aberto={aberto}
      titulo="Chamar apoio"
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao
            variante="primario"
            carregando={adicionar.isPending}
            disabled={!usuarioId}
            onClick={() =>
              adicionar.mutate(
                { chamadoId, dados: { usuarioId, comentario: comentario || undefined } },
                {
                  onSuccess: () => {
                    setUsuarioId('')
                    setComentario('')
                    aoFechar()
                  },
                },
              )
            }
          >
            Chamar
          </Botao>
        </>
      }
    >
      <div className="space-y-3">
        {erro ? <Aviso tipo="erro">{erro.corpo.mensagem}</Aviso> : null}
        <Aviso>
          O responsável continua o mesmo. O pedido vai para a pessoa, que aceita ou recusa sem você
          precisar esperar aqui. Quem entra de apoio ajuda na conversa, mas não encerra nem
          transfere: quando termina, sai pelo Fechar para mim.
        </Aviso>

        <div>
          <span className="mb-1 block text-sm font-medium text-tinta-800">
            De apoio nesta conversa
          </span>
          {apoiosAtuais.length === 0 && pedidosAtuais.length === 0 ? (
            <p className="text-sm text-tinta-400">Ninguém está de apoio nesta conversa.</p>
          ) : (
            <ul className="divide-y divide-tinta-100 rounded-md ring-1 ring-tinta-200">
              {apoiosAtuais.map((apoio) => (
                <li key={apoio.id} className="px-3 py-2 text-sm">
                  <span className="font-medium text-tinta-900">{apoio.nome}</span>
                  {apoio.comentario ? (
                    <span className="block text-xs text-tinta-500">{apoio.comentario}</span>
                  ) : null}
                </li>
              ))}
              {pedidosAtuais.map((pedido) => (
                <li key={pedido.id} className="px-3 py-2 text-sm">
                  <span className="font-medium text-tinta-900">{pedido.nome}</span>{' '}
                  <span className="text-xs text-amber-800">esperando resposta</span>
                  {pedido.comentario ? (
                    <span className="block text-xs text-tinta-500">{pedido.comentario}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-tinta-800">Quem vai apoiar</span>
          <select
            value={usuarioId}
            onChange={(e) => setUsuarioId(e.target.value)}
            className="w-full rounded-md ring-1 ring-tinta-200 bg-superficie px-3 py-2 text-sm"
          >
            <option value="">Escolha…</option>
            {(usuarios ?? [])
              .filter(
                (u) =>
                  u.ativo &&
                  // Pedir apoio a si mesmo não ajuda ninguém (mapa de uso, 08/10/2026).
                  u.id !== eu?.id &&
                  u.id !== chamado?.responsavelId &&
                  !apoiosAtuais.some((a) => a.usuarioId === u.id) &&
                  !pedidosAtuais.some((a) => a.usuarioId === u.id),
              )
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {nomeComStatus(u)}
                </option>
              ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-tinta-800">
            Comentário (opcional)
          </span>
          <textarea
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            rows={2}
            className="w-full rounded-md ring-1 ring-tinta-200 px-3 py-2 text-sm"
          />
        </label>
      </div>
    </Modal>
  )
}

/** Retorno agendado: sobe ao topo na hora marcada (5.1). */
function ModalRetorno({
  chamadoId,
  atual,
  aberto,
  aoFechar,
}: {
  chamadoId: string
  atual: string | null
  aberto: boolean
  aoFechar: () => void
}) {
  const agendar = useAgendarRetorno()
  const [quando, setQuando] = useState('')
  /*
    O campo nasce a cada abertura com o retorno desta conversa. Lido uma vez só, ao montar, ele
    guardava o da conversa aberta antes: a janela fica montada enquanto se troca de conversa.
  */
  const [abertoAntes, setAbertoAntes] = useState(false)
  if (aberto && !abertoAntes) {
    setAbertoAntes(true)
    setQuando(atual ? paraCampoDataHora(atual) : '')
  }
  if (!aberto && abertoAntes) setAbertoAntes(false)
  // A recusa de antes (data no passado, por exemplo) não reaparece na abertura seguinte.
  useEffect(() => {
    if (aberto) agendar.reset()
  }, [aberto])
  const erro = agendar.error instanceof ErroRequisicao ? agendar.error : null

  return (
    <Modal
      aberto={aberto}
      titulo="Agendar retorno"
      aoFechar={aoFechar}
      rodape={
        <>
          {atual ? (
            <Botao
              className="mr-auto"
              onClick={() => agendar.mutate({ chamadoId, quando: null }, { onSuccess: aoFechar })}
            >
              Cancelar retorno
            </Botao>
          ) : null}
          <Botao onClick={aoFechar}>Fechar</Botao>
          <Botao
            variante="primario"
            carregando={agendar.isPending}
            disabled={!quando}
            onClick={() =>
              agendar.mutate(
                { chamadoId, quando: new Date(quando).toISOString() },
                { onSuccess: aoFechar },
              )
            }
          >
            Agendar
          </Botao>
        </>
      }
    >
      <div className="space-y-3">
        {/* Sem isto, a recusa do servidor deixava o botão parado e a janela aberta, sem dizer por quê. */}
        {erro ? <Aviso tipo="erro">{erro.corpo.mensagem}</Aviso> : null}
        {/*
          O texto descrevia o comportamento antigo, de quando a data só mexia
          na ordem da lista. Hoje uma rotina cuida do prazo e reabre o
          atendimento, então encerrar depois de marcar o retorno virou o
          caminho normal — e quem lê aqui precisa saber que pode encerrar sem
          perder o combinado.
        */}
        <Aviso>
          Na hora marcada a conversa volta para você, reaberta se estiver encerrada, e você recebe
          um aviso. Nada é enviado ao cliente.
        </Aviso>
        <Campo
          rotulo="Quando voltar"
          type="datetime-local"
          value={quando}
          onChange={(e) => setQuando(e.target.value)}
        />
        <AvisoForaDoHorario quando={quando} oQue="A conversa volta para você" />
      </div>
    </Modal>
  )
}
